import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Box, CheckSquare, Layers, MapPin } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/layout/TopBar";
import { projectImage } from "@/components/admin/ProjectManager";

export default function ProjectsPage() {
  const { capabilities } = useAuth();
  const [projects, setProjects] = useState([]);
  const [myIssueCounts, setMyIssueCounts] = useState({});
  const [myTasks, setMyTasks] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api
      .get("/me/issues")
      .then((res) => {
        const counts = {};
        res.data.issues
          .filter((issue) => issue.status !== "Closed")
          .forEach((issue) => {
            counts[issue.project_id] = (counts[issue.project_id] || 0) + 1;
          });
        setMyIssueCounts(counts);
      })
      .catch(() => {});
    api
      .get("/me/tasks")
      .then((res) => {
        const grouped = {};
        res.data.tasks.forEach((task) => {
          grouped[task.project_id] = [...(grouped[task.project_id] || []), task];
        });
        setMyTasks(grouped);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-[#EEF2F8]">
      <TopBar />
      <main className="okn-page-enter mx-auto w-full max-w-[1500px] flex-1 px-4 py-10 sm:px-8 sm:py-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mono text-[11px] uppercase tracking-[0.3em] text-cyan-600">
              Digital Twin
            </p>
            <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
              Projects
            </h1>
            <p className="mt-3 max-w-lg text-sm text-slate-600 sm:text-base">
              You only see projects your account has been granted access to.
            </p>
          </div>
          {capabilities?.is_admin ? (
            <Link
              data-testid="manage-projects-link"
              to="/admin"
              className="okn-tool rounded-full border border-slate-300 bg-white px-5 py-2 text-[11px] uppercase tracking-wider text-slate-700 hover:bg-slate-100"
            >
              Manage projects
            </Link>
          ) : null}
        </div>

        {loading ? (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-64 animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div
            data-testid="projects-empty"
            className="mt-14 rounded-2xl border border-dashed border-slate-200 p-14 text-center"
          >
            <Layers className="mx-auto text-slate-500" />
            <p className="mt-4 text-sm text-slate-600">
              No projects are available for your account yet.
            </p>
          </div>
        ) : (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
            {projects.map((project, index) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}/overview`}
                data-testid={`project-card-${project.code}`}
                className="okn-tool group relative overflow-hidden rounded-2xl border border-slate-200 bg-white okn-rise"
                style={{ animationDelay: `${120 + index * 70}ms` }}
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={projectImage(project)}
                    alt={project.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent" />
                  <span className="mono absolute left-4 top-4 rounded bg-white/80 px-2 py-1 text-[11px] tracking-wider text-cyan-700 backdrop-blur">
                    {project.code}
                  </span>
                  {myIssueCounts[project.id] ? (
                    <span
                      data-testid={`my-open-issues-${project.code}`}
                      title="Open issues assigned to me"
                      className="mono absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-orange-500 px-2.5 py-1 text-[10.5px] font-medium text-white shadow-lg"
                    >
                      <AlertCircle size={12} /> {myIssueCounts[project.id]} for me
                    </span>
                  ) : null}
                  {myTasks[project.id]?.length ? (
                    <span
                      data-testid={`my-open-tasks-${project.code}`}
                      title="Open tasks assigned to me"
                      className="mono absolute right-4 flex items-center gap-1.5 rounded-full bg-cyan-600 px-2.5 py-1 text-[10.5px] font-medium text-white shadow-lg"
                      style={{ top: myIssueCounts[project.id] ? 46 : 16 }}
                    >
                      <CheckSquare size={12} /> {myTasks[project.id].length} task
                      {myTasks[project.id].length > 1 ? "s" : ""}
                    </span>
                  ) : null}
                </div>
                <div className="p-5">
                  <h2 className="font-display text-lg font-medium tracking-tight text-slate-900">
                    {project.name}
                  </h2>
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-600">
                    <MapPin size={13} /> {project.location || "—"}
                  </div>
                  <div className="mt-5 grid grid-cols-1 gap-3 border-t border-slate-200 pt-4 sm:grid-cols-2">
                    <div>
                      <p className="mono text-[10px] uppercase tracking-wider text-slate-500">
                        Active revision
                      </p>
                      <p className="mono mt-1 text-sm text-cyan-700">
                        {project.active_revision_name || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="mono text-[10px] uppercase tracking-wider text-slate-500">
                        Panels
                      </p>
                      <p className="mono mt-1 flex items-center gap-1.5 text-sm text-slate-800">
                        <Box size={12} className="text-slate-500" />
                        {project.panel_count || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <footer className="pb-8 text-center" data-testid="projects-footer">
        <p className="mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
          Powered by BIM Department
        </p>
        <p className="mono mt-1 text-[10px] tracking-[0.14em] text-slate-400">
          © 2026 Okyanus Grup · v1.5
        </p>
      </footer>
    </div>
  );
}
